import { Stack, Typography } from '@mui/material';
import { EvaluationType, useGetAreasOfLearningQuery } from '@schooly/api';
import { TagSelect } from '@schooly/style';
import { FC, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { DropdownSelectRow } from '../DropdownSelectRow';
import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { SelectContentSkeleton } from '../SelectContentSkeleton';
import { SelectSearchInput } from '../SelectSearchInput';
import { toggleMultipleValueArrayProperty } from '../utils';
import { AreaOfLearningTagSelect, AreaOfLearningTagSelectProps } from './AreaOfLearningTagSelect';

type EvaluationTypeExpandedSelectProps = PropsWithChildren<{
  schoolId: string;
  selectedEvaluationTypes: EvaluationType[];
  selectedAreaOfLearningIds: string[];
  onApply: (evaluationTypes: EvaluationType[], areaOfLearningIds: string[]) => void;
  onClear: () => void;
  onClose: () => void;
}>;

export const EvaluationTypeExpandedSelect: FC<EvaluationTypeExpandedSelectProps> = ({
  schoolId,
  selectedEvaluationTypes: initialSelectedEvaluationTypes,
  selectedAreaOfLearningIds: initialSelectedAreaOfLearningIds,
  onApply,
  onClose,
  onClear,
}) => {
  const { $t } = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');
  const [selectedEvaluationTypes, setSelectedEvaluationTypes] = useState<EvaluationType[]>(
    initialSelectedEvaluationTypes,
  );
  const [selectedAreaOfLearningIds, setSelectedAreaOfLearningIds] = useState<string[]>(
    initialSelectedAreaOfLearningIds,
  );

  const { data } = useGetAreasOfLearningQuery(schoolId, {
    enabled: !!schoolId,
    refetchOnMount: 'always',
  });

  const toggleEvaluationTypes = useCallback((v: EvaluationType[]) => {
    setSelectedEvaluationTypes((types) => toggleMultipleValueArrayProperty(types, v));
  }, []);

  const toggleAreaOfLearningIds = useCallback((v: string[]) => {
    setSelectedAreaOfLearningIds((ids) => toggleMultipleValueArrayProperty(ids, v));
  }, []);

  const handleApply = useCallback(() => {
    onApply(selectedEvaluationTypes, selectedAreaOfLearningIds);
    onClose();
  }, [selectedEvaluationTypes, selectedAreaOfLearningIds, onApply, onClose]);

  const tutorFeedbackLabel = $t({ id: 'assessments-TutorsFeedback' });
  const isTutorFeedbackSelected = selectedEvaluationTypes.includes(EvaluationType.TutorFeedback);

  const handleSelectAreaOfLearning = useCallback(
    (id: string) => {
      const isAreaOfLearningTypeSelected = selectedEvaluationTypes.includes(
        EvaluationType.AreaOfLearning,
      );
      const isSelectedId = selectedAreaOfLearningIds.includes(id);
      const shouldSelectAreaOfLearningType = !isSelectedId && !isAreaOfLearningTypeSelected;
      const shouldDeselectAreaOfLearningType =
        isSelectedId && selectedAreaOfLearningIds.length === 1 && isAreaOfLearningTypeSelected;

      if (shouldSelectAreaOfLearningType || shouldDeselectAreaOfLearningType) {
        toggleEvaluationTypes([EvaluationType.AreaOfLearning]);
      }

      toggleAreaOfLearningIds([id]);
    },
    [
      toggleAreaOfLearningIds,
      toggleEvaluationTypes,
      selectedAreaOfLearningIds,
      selectedEvaluationTypes,
    ],
  );

  const renderContent = useCallback(() => {
    if (!data) return <SelectContentSkeleton />;

    const areasOfLearning = data.filter((a) => a.name.toLowerCase().includes(query.toLowerCase()));
    const showTutorFeedback = tutorFeedbackLabel.toLowerCase().includes(query.toLowerCase());

    if (!areasOfLearning.length && !showTutorFeedback) {
      return (
        <Stack mb={2} gap={1}>
          <Typography variant="h3" color="common.grey2">
            <FormattedMessage id="input-NoOptionsFound" />
          </Typography>
          <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
        </Stack>
      );
    }

    return (
      <Stack mb={2} gap={1}>
        {showTutorFeedback && (
          <DropdownSelectRow
            key={tutorFeedbackLabel}
            isSelected={isTutorFeedbackSelected}
            onClick={() => toggleEvaluationTypes([EvaluationType.TutorFeedback])}
          >
            {tutorFeedbackLabel}
          </DropdownSelectRow>
        )}

        {areasOfLearning.map((a) => {
          const isSelected = selectedAreaOfLearningIds.includes(a.id);

          return (
            <DropdownSelectRow
              key={a.id}
              isSelected={isSelected}
              onClick={() => handleSelectAreaOfLearning(a.id)}
            >
              {a.name}
            </DropdownSelectRow>
          );
        })}
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [
    data,
    handleSelectAreaOfLearning,
    isTutorFeedbackSelected,
    toggleEvaluationTypes,
    query,
    selectedAreaOfLearningIds,
    tutorFeedbackLabel,
    handleApply,
    onClear,
  ]);

  return (
    <ExpandedSelect
      hasSelectedValue={isTutorFeedbackSelected || selectedAreaOfLearningIds.length > 0}
      onClose={onClose}
      onClear={onClear}
      renderContent={renderContent}
      showNextPageLoading={false}
    >
      {isTutorFeedbackSelected && (
        <TagSelect
          key={tutorFeedbackLabel}
          label={tutorFeedbackLabel}
          size="small"
          sx={{ maxWidth: 200 }}
          onDelete={() => toggleEvaluationTypes([EvaluationType.TutorFeedback])}
        />
      )}

      {renderAreaOfLearningTags({
        ids: selectedAreaOfLearningIds,
        onDelete: (id) => toggleAreaOfLearningIds([id]),
        tagProps: { schoolId, size: 'small', sx: { maxWidth: 200 } },
      })}

      <SelectSearchInput ref={inputRef} autoFocus value={query} onChangeText={setQuery} />
    </ExpandedSelect>
  );
};

type RenderAreaOfLearningTagsParams = {
  ids: string[];
  onDelete?: (v: string) => void;
  onClick?: (v: string) => void;
  tagProps:
    | Omit<AreaOfLearningTagSelectProps, 'id'>
    | ((v: string) => Omit<AreaOfLearningTagSelectProps, 'id'>);
};

export const renderAreaOfLearningTags = ({
  ids,
  onDelete,
  onClick,
  tagProps,
}: RenderAreaOfLearningTagsParams) => {
  return ids.map((v) => (
    <AreaOfLearningTagSelect
      key={v}
      id={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
};
